import React, { useMemo, useState } from "react";
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { formatScore, SeverityBadge } from "./TrendExplanationDetail";

// Small badge + icon per theme-group label — New/Improved read as positive
// (green "+"), Intensified/Worsened as a warning (orange "!"), Faded as
// neutral fade-out (gray "i"). Falls back to the neutral style for any label
// this doesn't recognize rather than guessing a sentiment for it.
const GROUP_STYLES = {
  New: { bg: "#1b7f4d", icon: "+" },
  Improved: { bg: "#1b7f4d", icon: "+" },
  Intensified: { bg: "#c0702b", icon: "!" },
  Worsened: { bg: "#c0352b", icon: "!" },
  Faded: { bg: "#667085", icon: "i" },
};
const DEFAULT_GROUP_STYLE = { bg: "#667085", icon: "i" };

function formatDateShort(dateStr) {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// analysis is normally an array of short bullets; defensively wrap a stray
// plain string (old pre-backfill data) as a single-item array, same guard
// TrendExplanationDetail.js uses.
function normalizeAnalysis(analysis) {
  const arr = Array.isArray(analysis) ? analysis : [analysis];
  return arr.filter(Boolean);
}

function ChangeCell({ fromScore, toScore }) {
  const delta = toScore - fromScore;
  const isDecline = delta < 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, color: isDecline ? "#c0352b" : "#1b7f4d", fontWeight: 700, fontSize: 13 }}>
      <Box component="span" sx={{ fontSize: 10 }}>{isDecline ? "▼" : "▲"}</Box>
      {Math.abs(delta).toFixed(1)}
    </Box>
  );
}

function GroupColumn({ label, items }) {
  if (!items?.length) return null;
  const style = GROUP_STYLES[label] || DEFAULT_GROUP_STYLE;
  return (
    <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
        <Box sx={{ width: 20, height: 20, borderRadius: "4px", bgcolor: style.bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
          {style.icon}
        </Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#1a2027" }}>{label}</Typography>
        <Box sx={{ fontSize: 11, fontWeight: 700, color: "#667085", bgcolor: "#eef1f4", borderRadius: "10px", px: 0.8, minWidth: 16, textAlign: "center" }}>
          {items.length}
        </Box>
      </Box>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {items.map((item, i) => (
          <Box component="li" key={i} sx={{ fontSize: 12.5, lineHeight: 1.6, color: "#344054", mb: 0.4 }}>
            {item}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const headCellSx = { border: "none", borderBottom: "1px solid #eef1f4", p: "8px 6px", fontSize: 11.5, fontWeight: 700, color: "#667085" };
const bodyCellSx = { border: "none", borderBottom: "1px solid #f5f6f8", p: "10px 6px", verticalAlign: "top" };

/**
 * "What Changed Since Last Run" — table of only the lines (Overall + whichever
 * topics qualified) whose sentiment score moved by >=0.5 from the previous
 * run. Click a row (or its chevron) to expand the full "why it moved"
 * paragraph plus the New/Intensified/Faded (or Worsened/Improved, for
 * Overall) theme breakdown — same `groups`/`analysis` data
 * TrendExplanationDetail.js renders for TrendChart's click-to-explain modal,
 * reshaped into this table's column layout rather than shared JSX, since the
 * two surfaces' layouts are different enough (badge+column grid here vs.
 * stacked sections there) that forcing one render path would fight both.
 *
 * `rows` is already filtered, ordered (Overall first, then canonical topic
 * order, including any dynamic/per-customer topics that qualified), labeled,
 * and colored to match the trend chart's own line colors — all by the caller
 * (ConversationsResultsV3) — this component only renders.
 *
 * Two distinct empty states, not one: a series' very first run has nothing
 * to compare against at all (isEarliestRun), which is a different situation
 * from a later run genuinely being compared and found stable. Conflating
 * them into one "nothing changed" message would misrepresent the first case
 * as a confirmed-stable reading when really no comparison happened.
 */
export default function WhatChangedSection({ rows, isEarliestRun }) {
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [sortBy, setSortBy] = useState("change");
  const [sortDir, setSortDir] = useState("desc");

  const toggleRow = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allExpanded = rows.length > 0 && rows.every((r) => expandedKeys.has(r.key));
  const toggleExpandAll = () => setExpandedKeys(allExpanded ? new Set() : new Set(rows.map((r) => r.key)));

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir(column === "topic" ? "asc" : "desc");
    }
  };

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    if (sortBy === "topic") {
      arr.sort((a, b) => a.label.localeCompare(b.label) * (sortDir === "asc" ? 1 : -1));
    } else if (sortBy === "change") {
      arr.sort((a, b) => {
        const da = Math.abs(a.explanation.toScore - a.explanation.fromScore);
        const db = Math.abs(b.explanation.toScore - b.explanation.fromScore);
        return (da - db) * (sortDir === "asc" ? 1 : -1);
      });
    }
    return arr;
  }, [rows, sortBy, sortDir]);

  let emptyMessage = null;
  if (rows.length === 0) {
    emptyMessage = isEarliestRun
      ? "This is the earliest tracked run for this series — nothing to compare yet."
      : "No significant shifts since the previous run.";
  }

  const firstExplanation = rows[0]?.explanation;
  const dateRangeLabel = firstExplanation
    ? `${formatDateShort(firstExplanation.fromDate)} → ${formatDateShort(firstExplanation.toDate)} · `
    : "";

  return (
    <Box id="cm-tour-what-changed" sx={{ bgcolor: "#fff", borderRadius: "8px", boxShadow: "0px 3.5px 5.5px 0px rgba(0,0,0,0.05)", p: "22px 26px" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1a2027", mb: 0.25 }}>What Changed Since Last Run</Typography>
          <Typography sx={{ fontSize: 12.5, color: "#667085" }}>
            {dateRangeLabel}topics that moved 0.5 or more{rows.length > 0 ? " · click a row for details" : ""}
          </Typography>
        </Box>
        {rows.length > 0 && (
          <Typography
            onClick={toggleExpandAll}
            sx={{ fontSize: 12.5, fontWeight: 600, color: "#1976d2", cursor: "pointer", flexShrink: 0, userSelect: "none", "&:hover": { textDecoration: "underline" } }}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </Typography>
        )}
      </Box>

      {emptyMessage ? (
        <Typography sx={{ fontSize: 13, color: "#667085", py: 1, mt: 1.5 }}>{emptyMessage}</Typography>
      ) : (
        <Table sx={{ mt: 1.5 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 32 }} />
              <TableCell sx={headCellSx}>
                <TableSortLabel active={sortBy === "topic"} direction={sortBy === "topic" ? sortDir : "asc"} onClick={() => handleSort("topic")}>
                  Topic
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headCellSx}>{firstExplanation ? formatDateShort(firstExplanation.fromDate) : "Before"}</TableCell>
              <TableCell sx={headCellSx}>{firstExplanation ? formatDateShort(firstExplanation.toDate) : "After"}</TableCell>
              <TableCell sx={headCellSx}>
                <TableSortLabel active={sortBy === "change"} direction={sortBy === "change" ? sortDir : "desc"} onClick={() => handleSort("change")}>
                  Change
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={headCellSx}>Why it moved</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row) => {
              const isExpanded = expandedKeys.has(row.key);
              const exp = row.explanation;
              const whyBullets = normalizeAnalysis(exp.analysis);
              const whyPreview = whyBullets.join(" "); // single line for the truncated table cell
              return (
                <React.Fragment key={row.key}>
                  <TableRow onClick={() => toggleRow(row.key)} sx={{ cursor: "pointer", "&:hover": { bgcolor: "#fafbfc" } }}>
                    <TableCell sx={bodyCellSx}>
                      <KeyboardArrowRightIcon sx={{ fontSize: 18, color: "#98a2b3", transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
                        <Box sx={{ width: 9, height: 9, borderRadius: row.key === "overall" ? "2px" : "50%", bgcolor: row.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1a2027", whiteSpace: "nowrap" }}>{row.label}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontSize: 13, color: "#475467" }}>{formatScore(exp.fromScore)}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontSize: 13, color: "#475467" }}>{formatScore(exp.toScore)}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <ChangeCell fromScore={exp.fromScore} toScore={exp.toScore} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <SeverityBadge fromScore={exp.fromScore} toScore={exp.toScore} />
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, maxWidth: 260 }}>
                      <Typography sx={{ fontSize: 12.5, color: "#667085", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {whyPreview}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ border: "none", borderBottom: "1px solid #f5f6f8", p: "0 6px 20px" }}>
                        <Box sx={{ borderLeft: `4px solid ${row.color}`, bgcolor: "#fafbfc", borderRadius: "0 6px 6px 0", p: "12px 16px", mb: 2 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#667085", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
                            Why it moved
                          </Typography>
                          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {whyBullets.map((point, i) => (
                              <Box component="li" key={i} sx={{ fontSize: 13, lineHeight: 1.6, color: "#344054", mb: i === whyBullets.length - 1 ? 0 : 0.4 }}>
                                {point}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                          {(exp.groups || []).map((group) => (
                            <GroupColumn key={group.label} label={group.label} items={group.items} />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
